import { useEffect, useRef, useState } from "react";

/**
 * useDebouncedField — entrada controlada con commit debounced al backend.
 *
 * - `value` y `onChange` se devuelven para vincular al input/textarea.
 * - El cambio del state local es INMEDIATO (no se nota lag al teclear).
 * - El commit (llamada a `onCommit`) ocurre `delay` ms después de la última
 *   pulsación. Por defecto 500ms — suficiente para no saturar al backend
 *   pero rápido para que no se pierda nada si el usuario cambia de pestaña.
 *
 * Soporta sincronización si `initial` cambia desde fuera (p.ej. una recarga
 * del expediente refresca el campo) — siempre que no haya un cambio local
 * pendiente más reciente.
 *
 * @example
 *   const [notas, onChange, flush] = useDebouncedField(
 *     exp.observaciones_especiales,
 *     (v) => onUpdateExpediente("observaciones_especiales", v),
 *   );
 *   <textarea value={notas} onChange={onChange} onBlur={flush} />
 */
export function useDebouncedField(initial, onCommit, delay = 500) {
  const [value, setValue] = useState(initial ?? "");
  const timerRef = useRef(null);
  const lastCommittedRef = useRef(initial ?? "");
  const onCommitRef = useRef(onCommit);
  onCommitRef.current = onCommit;

  // Sincronizar cuando el valor externo cambia desde fuera (recarga del
  // expediente, dictado por voz, etc.) — solo si no estamos mid-typing.
  useEffect(() => {
    const v = initial ?? "";
    if (v === value) return;
    // Si el usuario está tecleando (hay un timer pendiente) ignoramos el
    // cambio externo para no robarle el foco/contenido.
    if (timerRef.current) return;
    if (v !== lastCommittedRef.current) {
      setValue(v);
      lastCommittedRef.current = v;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initial]);

  const onChange = (e) => {
    const v = typeof e === "string" ? e : e.target.value;
    setValue(v);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      lastCommittedRef.current = v;
      onCommitRef.current?.(v);
    }, delay);
  };

  // flush — fuerza commit inmediato (útil en onBlur)
  const flush = () => {
    if (!timerRef.current) return;
    clearTimeout(timerRef.current);
    timerRef.current = null;
    lastCommittedRef.current = value;
    onCommitRef.current?.(value);
  };

  // Cleanup al desmontar — commit pendiente si lo hay
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        // No commit en unmount: ya lo manejará el padre si re-monta.
      }
    };
  }, []);

  return [value, onChange, flush];
}
